'use client';

import React, { useState, useEffect, Suspense, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';
import BookingModal from './components/BookingModal';

interface Property {
  id: number;
  name: string;
  location: string;
  country: string;
  type: string;
  rating: number;
  reviews: number;
  price: number;
  currency: string;
  images: { src: string; alt: string }[];
  badge?: string;
  isSuperhost?: boolean;
  amenities: string[];
  lat: number;
  lng: number;
  maxGuests: number;
  bedrooms: number;
}

const ALL_PROPERTIES: Property[] = [
  {
    id: 1,
    name: 'Acacia Luxury Apartments',
    location: 'Kololo, Kampala',
    country: 'Uganda',
    type: 'Serviced Apartment',
    rating: 4.87,
    reviews: 124,
    price: 180000,
    currency: 'UGX',
    badge: 'Guest favourite',
    isSuperhost: true,
    amenities: ['Wi-Fi', 'Pool', 'Parking', 'AC'],
    lat: 0.3476,
    lng: 32.5825,
    maxGuests: 4,
    bedrooms: 2,
    images: [
      { src: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80', alt: 'Acacia Luxury Apartments living area with panoramic Kampala views' },
      { src: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800&q=80', alt: 'Master bedroom with king-size bed' },
    ],
  },
  {
    id: 2,
    name: 'Entebbe Lakeside Villa',
    location: 'Entebbe',
    country: 'Uganda',
    type: 'Villa',
    rating: 4.95,
    reviews: 89,
    price: 350000,
    currency: 'UGX',
    badge: 'Superhost',
    isSuperhost: true,
    amenities: ['Wi-Fi', 'Lake View', 'Garden', 'BBQ'],
    lat: 0.0512,
    lng: 32.4637,
    maxGuests: 8,
    bedrooms: 4,
    images: [
      { src: 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=800&q=80', alt: 'Entebbe Lakeside Villa exterior with Lake Victoria views' },
      { src: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&q=80', alt: 'Villa pool overlooking the lake at sunset' },
    ],
  },
  {
    id: 3,
    name: 'Jinja Nile View Lodge',
    location: 'Jinja',
    country: 'Uganda',
    type: 'Lodge',
    rating: 4.78,
    reviews: 203,
    price: 220000,
    currency: 'UGX',
    amenities: ['Wi-Fi', 'River View', 'Restaurant', 'Tours'],
    lat: 0.4478,
    lng: 33.2026,
    maxGuests: 3,
    bedrooms: 1,
    images: [
      { src: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80', alt: 'Jinja Nile View Lodge with River Nile panorama' },
      { src: 'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=800&q=80', alt: 'Lodge balcony overlooking the Nile' },
    ],
  },
  {
    id: 4,
    name: 'Bwindi Forest Retreat',
    location: 'Bwindi, Kabale',
    country: 'Uganda',
    type: 'Eco Lodge',
    rating: 4.92,
    reviews: 67,
    price: 480000,
    currency: 'UGX',
    badge: 'Guest favourite',
    isSuperhost: true,
    amenities: ['Wi-Fi', 'Forest View', 'Gorilla Trekking', 'Breakfast'],
    lat: -1.0333,
    lng: 29.6833,
    maxGuests: 2,
    bedrooms: 1,
    images: [
      { src: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800&q=80', alt: 'Bwindi Forest Retreat nestled in the impenetrable forest' },
      { src: 'https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=800&q=80', alt: 'Eco lodge interior with forest views' },
    ],
  },
  {
    id: 5,
    name: 'Kampala City Loft',
    location: 'Nakasero, Kampala',
    country: 'Uganda',
    type: 'Apartment',
    rating: 4.65,
    reviews: 312,
    price: 120000,
    currency: 'UGX',
    amenities: ['Wi-Fi', 'City View', 'Gym', 'Rooftop'],
    lat: 0.3163,
    lng: 32.5822,
    maxGuests: 2,
    bedrooms: 1,
    images: [
      { src: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&q=80', alt: 'Modern Kampala City Loft with downtown views' },
      { src: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&q=80', alt: 'Open-plan living space with city skyline backdrop' },
    ],
  },
  {
    id: 6,
    name: 'Murchison Safari Camp',
    location: 'Murchison Falls',
    country: 'Uganda',
    type: 'Safari Camp',
    rating: 4.88,
    reviews: 145,
    price: 550000,
    currency: 'UGX',
    badge: 'Superhost',
    isSuperhost: true,
    amenities: ['Game Drives', 'Full Board', 'River Cruise', 'Wi-Fi'],
    lat: 2.2833,
    lng: 31.7333,
    maxGuests: 2,
    bedrooms: 1,
    images: [
      { src: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800&q=80', alt: 'Murchison Safari Camp luxury tents overlooking the savanna' },
      { src: 'https://images.unsplash.com/photo-1493246507139-91e8fad9978e?w=800&q=80', alt: 'Safari camp dining area with wildlife views' },
    ],
  },
  {
    id: 7,
    name: 'Ssese Islands Beach House',
    location: 'Ssese Islands',
    country: 'Uganda',
    type: 'Beach House',
    rating: 4.82,
    reviews: 98,
    price: 290000,
    currency: 'UGX',
    amenities: ['Beach Access', 'Kayaking', 'Wi-Fi', 'BBQ'],
    lat: -0.4167,
    lng: 32.0833,
    maxGuests: 6,
    bedrooms: 3,
    images: [
      { src: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=800&q=80', alt: 'Ssese Islands Beach House with private beach access on Lake Victoria' },
      { src: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800&q=80', alt: 'Beach house deck overlooking the lake at sunrise' },
    ],
  },
  {
    id: 8,
    name: 'Fort Portal Mountain Cottage',
    location: 'Fort Portal',
    country: 'Uganda',
    type: 'Cottage',
    rating: 4.73,
    reviews: 56,
    price: 160000,
    currency: 'UGX',
    amenities: ['Mountain View', 'Fireplace', 'Garden', 'Wi-Fi'],
    lat: 0.6667,
    lng: 30.2833,
    maxGuests: 4,
    bedrooms: 2,
    images: [
      { src: 'https://images.unsplash.com/photo-1510798831971-661eb04b3739?w=800&q=80', alt: 'Fort Portal Mountain Cottage surrounded by lush crater lakes' },
      { src: 'https://images.unsplash.com/photo-1449158743715-0a90ebb6d2d8?w=800&q=80', alt: 'Cozy cottage interior with mountain views' },
    ],
  },
];

const PROPERTY_TYPES = ['All', 'Apartment', 'Villa', 'Lodge', 'Eco Lodge', 'Safari Camp', 'Beach House', 'Cottage', 'Serviced Apartment'];
const AMENITY_FILTERS = ['Wi-Fi', 'Pool', 'Parking', 'AC', 'Breakfast', 'Gym', 'BBQ', 'Garden'];
const SORT_OPTIONS = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'rating', label: 'Top Rated' },
  { value: 'reviews', label: 'Most Reviewed' },
];

interface FilterPanelProps {
  superhostOnly: boolean;
  setSuperhostOnly: (v: boolean) => void;
  selectedType: string;
  setSelectedType: (v: string) => void;
  priceRange: [number, number];
  setPriceRange: (v: [number, number]) => void;
  minRating: number;
  setMinRating: (v: number) => void;
  minGuests: number;
  setMinGuests: (v: number) => void;
  minBedrooms: number;
  setMinBedrooms: (v: number) => void;
  selectedAmenities: string[];
  toggleAmenity: (a: string) => void;
  activeFilterCount: number;
  clearFilters: () => void;
}

function FilterPanel({
  superhostOnly, setSuperhostOnly,
  selectedType, setSelectedType,
  priceRange, setPriceRange,
  minRating, setMinRating,
  minGuests, setMinGuests,
  minBedrooms, setMinBedrooms,
  selectedAmenities, toggleAmenity,
  activeFilterCount, clearFilters,
}: FilterPanelProps) {
  return (
    <div className="space-y-6">
      {/* Superhost */}
      <label className="flex items-center justify-between cursor-pointer group">
        <div>
          <p className="text-sm font-bold text-gray-900">Superhost only</p>
          <p className="text-xs text-gray-500">Top-rated, experienced hosts</p>
        </div>
        <div
          onClick={() => setSuperhostOnly(!superhostOnly)}
          className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${superhostOnly ? 'bg-[#4088C6]' : 'bg-gray-200'}`}
        >
          <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${superhostOnly ? 'translate-x-5' : ''}`} />
        </div>
      </label>

      {/* Property type */}
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-3">Property Type</h3>
        <div className="flex flex-wrap gap-2">
          {PROPERTY_TYPES.map((t) => (
            <button
              key={t}
              onClick={() => setSelectedType(t)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                selectedType === t ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Price range */}
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-1">Price Range</h3>
        <p className="text-xs text-gray-500 mb-3">UGX {priceRange[0].toLocaleString()} – {priceRange[1].toLocaleString()}</p>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 w-6">Min</span>
            <input
              type="range"
              min={0}
              max={600000}
              step={10000}
              value={priceRange[0]}
              onChange={(e) => setPriceRange([Math.min(Number(e.target.value), priceRange[1] - 10000), priceRange[1]])}
              className="flex-1 accent-[#4088C6]"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 w-6">Max</span>
            <input
              type="range"
              min={0}
              max={600000}
              step={10000}
              value={priceRange[1]}
              onChange={(e) => setPriceRange([priceRange[0], Math.max(Number(e.target.value), priceRange[0] + 10000)])}
              className="flex-1 accent-[#4088C6]"
            />
          </div>
        </div>
      </div>

      {/* Min rating */}
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-3">Minimum Rating</h3>
        <div className="flex gap-2 flex-wrap">
          {[0, 4, 4.5, 4.8].map((r) => (
            <button
              key={r}
              onClick={() => setMinRating(r)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                minRating === r ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
            >
              {r === 0 ? 'Any' : `${r}+ ★`}
            </button>
          ))}
        </div>
      </div>

      {/* Guests */}
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-3">Minimum Guests</h3>
        <div className="flex gap-2">
          {[1, 2, 4, 6].map((g) => (
            <button
              key={g}
              onClick={() => setMinGuests(g)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                minGuests === g ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
            >
              {g}+
            </button>
          ))}
        </div>
      </div>

      {/* Bedrooms */}
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-3">Bedrooms</h3>
        <div className="flex gap-2">
          {[0, 1, 2, 3].map((b) => (
            <button
              key={b}
              onClick={() => setMinBedrooms(b)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                minBedrooms === b ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
            >
              {b === 0 ? 'Any' : `${b}+`}
            </button>
          ))}
        </div>
      </div>

      {/* Amenities */}
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-3">Amenities</h3>
        <div className="grid grid-cols-2 gap-2">
          {AMENITY_FILTERS.map((a) => (
            <label key={a} className="flex items-center gap-2 cursor-pointer group">
              <input
                type="checkbox"
                checked={selectedAmenities.includes(a)}
                onChange={() => toggleAmenity(a)}
                className="w-4 h-4 rounded accent-[#4088C6]"
              />
              <span className="text-sm text-gray-700 group-hover:text-gray-900">{a}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Clear filters */}
      {activeFilterCount > 0 && (
        <button
          onClick={clearFilters}
          className="w-full text-sm font-semibold text-[#4088C6] hover:text-[#3070a6] border border-[#4088C6]/30 hover:border-[#4088C6] py-2.5 rounded-xl transition-all"
        >
          Clear all filters ({activeFilterCount})
        </button>
      )}
    </div>
  );
}

function SearchResultsContent() {
  const searchParams = useSearchParams();
  const initialQuery = searchParams.get('q') || '';

  const [searchInput, setSearchInput] = useState(initialQuery);
  const [activeQuery, setActiveQuery] = useState(initialQuery);
  const [selectedType, setSelectedType] = useState('All');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 600000]);
  const [minRating, setMinRating] = useState(0);
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);
  const [minGuests, setMinGuests] = useState(1);
  const [minBedrooms, setMinBedrooms] = useState(0);
  const [superhostOnly, setSuperhostOnly] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [sortBy, setSortBy] = useState<'recommended' | 'price_asc' | 'price_desc' | 'rating' | 'reviews'>('recommended');
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [bookingProperty, setBookingProperty] = useState<Property | null>(null);
  const [likedIds, setLikedIds] = useState<Set<number>>(new Set());
  const [imgIndexes, setImgIndexes] = useState<Record<number, number>>({});
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const activeFilterCount = [
    selectedType !== 'All',
    minRating > 0,
    selectedAmenities.length > 0,
    priceRange[1] < 600000 || priceRange[0] > 0,
    minGuests > 1,
    minBedrooms > 0,
    superhostOnly,
  ].filter(Boolean).length;

  const toggleAmenity = (a: string) => {
    setSelectedAmenities((prev) =>
      prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]
    );
  };

  const toggleLike = (e: React.MouseEvent, id: number) => {
    e.preventDefault();
    e.stopPropagation();
    setLikedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const prevImg = (e: React.MouseEvent, id: number, len: number) => {
    e.preventDefault();
    e.stopPropagation();
    setImgIndexes((prev) => ({ ...prev, [id]: ((prev[id] ?? 0) - 1 + len) % len }));
  };

  const nextImg = (e: React.MouseEvent, id: number, len: number) => {
    e.preventDefault();
    e.stopPropagation();
    setImgIndexes((prev) => ({ ...prev, [id]: ((prev[id] ?? 0) + 1) % len }));
  };

  const clearFilters = () => {
    setSelectedType('All');
    setMinRating(0);
    setSelectedAmenities([]);
    setPriceRange([0, 600000]);
    setMinGuests(1);
    setMinBedrooms(0);
    setSuperhostOnly(false);
  };

  const handleSearch = () => {
    setActiveQuery(searchInput);
  };

  const filtered = ALL_PROPERTIES.filter((p) => {
    const q = activeQuery.toLowerCase();
    if (q && !p.name.toLowerCase().includes(q) && !p.location.toLowerCase().includes(q) && !p.type.toLowerCase().includes(q)) return false;
    if (selectedType !== 'All' && p.type !== selectedType) return false;
    if (p.price < priceRange[0] || p.price > priceRange[1]) return false;
    if (p.rating < minRating) return false;
    if (selectedAmenities.length > 0 && !selectedAmenities.every((a) => p.amenities.includes(a))) return false;
    if (p.maxGuests < minGuests) return false;
    if (p.bedrooms < minBedrooms) return false;
    if (superhostOnly && !p.isSuperhost) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'price_asc') return a.price - b.price;
    if (sortBy === 'price_desc') return b.price - a.price;
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'reviews') return b.reviews - a.reviews;
    return 0;
  });

  const filterPanelProps: FilterPanelProps = {
    superhostOnly, setSuperhostOnly,
    selectedType, setSelectedType,
    priceRange, setPriceRange,
    minRating, setMinRating,
    minGuests, setMinGuests,
    minBedrooms, setMinBedrooms,
    selectedAmenities, toggleAmenity,
    activeFilterCount, clearFilters,
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">

          {/* Enhanced search bar */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search by name, location, or type…"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40 bg-gray-50 focus:bg-white transition-all"
                />
                {searchInput && (
                  <button
                    onClick={() => { setSearchInput(''); setActiveQuery(''); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                )}
              </div>
              <button
                onClick={handleSearch}
                className="bg-[#4088C6] hover:bg-[#2D6FA8] text-white font-semibold px-6 py-3 rounded-xl text-sm transition-all flex items-center gap-2 flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search
              </button>
            </div>
          </div>

          {/* Top bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {activeQuery ? `Results for "${activeQuery}"` : 'All Properties'}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">{sorted.length} propert{sorted.length !== 1 ? 'ies' : 'y'} found</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Mobile filter button */}
              <button
                onClick={() => setShowMobileFilters(true)}
                className="lg:hidden flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border border-gray-200 bg-white hover:border-gray-400 transition-all relative"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
                Filters
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#4088C6] text-white text-xs font-bold rounded-full flex items-center justify-center">{activeFilterCount}</span>
                )}
              </button>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                className="text-sm border border-gray-200 rounded-full px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40 cursor-pointer"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>

              {/* View mode */}
              <div className="flex items-center border border-gray-200 rounded-full overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                  aria-label="Grid view"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                  aria-label="List view"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                </button>
              </div>

              {/* Map toggle */}
              <button
                onClick={() => setShowMap(!showMap)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                  showMap ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                {showMap ? 'Hide Map' : 'Map'}
              </button>
            </div>
          </div>

          {/* Active filter chips */}
          {activeFilterCount > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {selectedType !== 'All' && (
                <span className="flex items-center gap-1.5 bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                  {selectedType}
                  <button onClick={() => setSelectedType('All')} className="hover:text-gray-900">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </span>
              )}
              {minRating > 0 && (
                <span className="flex items-center gap-1.5 bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                  {minRating}+ ★
                  <button onClick={() => setMinRating(0)} className="hover:text-gray-900">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </span>
              )}
              {superhostOnly && (
                <span className="flex items-center gap-1.5 bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                  Superhost
                  <button onClick={() => setSuperhostOnly(false)} className="hover:text-gray-900">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </span>
              )}
              {selectedAmenities.map((a) => (
                <span key={a} className="flex items-center gap-1.5 bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                  {a}
                  <button onClick={() => toggleAmenity(a)} className="hover:text-gray-900">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </span>
              ))}
              <button onClick={clearFilters} className="text-xs font-semibold text-[#4088C6] hover:text-[#3070a6] px-2 py-1.5 underline">
                Clear all
              </button>
            </div>
          )}

          <div className="flex gap-6">
            {/* Sidebar filters (desktop) */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-28 bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-sm font-bold text-gray-900">Filters</h2>
                  {activeFilterCount > 0 && (
                    <span className="text-xs font-bold bg-[#4088C6]/10 text-[#4088C6] px-2 py-0.5 rounded-full">{activeFilterCount} active</span>
                  )}
                </div>
                <FilterPanel {...filterPanelProps} />
              </div>
            </aside>

            {/* Main content */}
            <div className="flex-1 min-w-0">
              {showMap && (
                <div className="mb-6 rounded-2xl overflow-hidden border border-gray-200 bg-gray-100 h-72 relative flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50" />
                  <div className="absolute inset-0">
                    {sorted.map((p) => (
                      <div
                        key={p.id}
                        onMouseEnter={() => setHoveredId(p.id)}
                        onMouseLeave={() => setHoveredId(null)}
                        style={{
                          left: `${((p.lng - 29.5) / 5) * 100}%`,
                          top: `${((1.5 - p.lat) / 4) * 100}%`,
                        }}
                        className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                      >
                        <div className={`px-2 py-1 rounded-full text-xs font-bold shadow-md transition-all ${
                          hoveredId === p.id ? 'bg-gray-900 text-white scale-110' : 'bg-white text-gray-900 border border-gray-200'
                        }`}>
                          UGX {(p.price / 1000).toFixed(0)}k
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="relative z-10 text-center">
                    <div className="text-gray-400 text-sm font-medium bg-white/80 px-4 py-2 rounded-full">
                      Interactive map — {sorted.length} properties shown
                    </div>
                  </div>
                </div>
              )}

              {sorted.length === 0 ? (
                <div className="text-center py-24">
                  <div className="text-5xl mb-4">🔍</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No properties found</h3>
                  <p className="text-gray-500 mb-4">Try adjusting your filters or search term</p>
                  <button
                    onClick={clearFilters}
                    className="text-sm font-semibold text-[#4088C6] hover:text-[#3070a6] border border-[#4088C6]/30 px-5 py-2.5 rounded-full transition-all"
                  >
                    Clear all filters
                  </button>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {sorted.map((property) => {
                    const imgIdx = imgIndexes[property.id] ?? 0;
                    const liked = likedIds.has(property.id);
                    return (
                      <div
                        key={property.id}
                        className="group cursor-pointer"
                        onMouseEnter={() => setHoveredId(property.id)}
                        onMouseLeave={() => setHoveredId(null)}
                      >
                        <div className="relative aspect-square rounded-2xl overflow-hidden bg-gray-100 mb-3">
                          <AppImage
                            src={property.images[imgIdx].src}
                            alt={property.images[imgIdx].alt}
                            fill
                            className="object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                          {property.badge && (
                            <div className="absolute top-3 left-3 bg-white text-gray-800 text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">
                              {property.badge}
                            </div>
                          )}
                          <button
                            onClick={(e) => toggleLike(e, property.id)}
                            className="absolute top-3 right-3 p-1.5 transition-transform hover:scale-110"
                          >
                            <svg
                              className={`w-6 h-6 drop-shadow-md ${liked ? 'text-[#4088C6] fill-[#4088C6]' : 'text-white fill-white/30 stroke-white'}`}
                              viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                            </svg>
                          </button>
                          {property.images.length > 1 && (
                            <>
                              <button onClick={(e) => prevImg(e, property.id, property.images.length)} className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity">
                                <svg className="w-3.5 h-3.5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
                              </button>
                              <button onClick={(e) => nextImg(e, property.id, property.images.length)} className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity">
                                <svg className="w-3.5 h-3.5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
                              </button>
                            </>
                          )}
                        </div>
                        <div onClick={() => window.location.href = `/property/${property.id}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 mb-0.5">
                                {property.isSuperhost && (
                                  <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">Superhost</span>
                                )}
                                <h3 className="text-sm font-semibold text-gray-900 truncate">{property.name}</h3>
                              </div>
                              <p className="text-sm text-gray-500 truncate">{property.location}, {property.country}</p>
                              <p className="text-xs text-gray-400">{property.type} · {property.bedrooms} bed · {property.maxGuests} guests</p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <svg className="w-3.5 h-3.5 text-gray-900 fill-gray-900" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              <span className="text-sm font-semibold text-gray-900">{property.rating.toFixed(2)}</span>
                              <span className="text-xs text-gray-400">({property.reviews})</span>
                            </div>
                          </div>
                          <div className="mt-1.5 flex items-center justify-between">
                            <div>
                              <span className="text-sm font-bold text-gray-900">{property.currency} {property.price.toLocaleString()}</span>
                              <span className="text-sm text-gray-500"> / night</span>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); setBookingProperty(property); }}
                              className="text-xs font-semibold text-[#4088C6] hover:text-[#3070a6] border border-[#4088C6]/30 hover:border-[#4088C6] px-3 py-1 rounded-full transition-all"
                            >
                              Reserve
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* List view */
                <div className="space-y-4">
                  {sorted.map((property) => {
                    const imgIdx = imgIndexes[property.id] ?? 0;
                    const liked = likedIds.has(property.id);
                    return (
                      <div
                        key={property.id}
                        className="group flex gap-4 bg-white border border-gray-100 rounded-2xl p-4 hover:shadow-md transition-shadow cursor-pointer"
                        onMouseEnter={() => setHoveredId(property.id)}
                        onMouseLeave={() => setHoveredId(null)}
                      >
                        <div className="relative w-40 h-32 rounded-xl overflow-hidden flex-shrink-0">
                          <AppImage
                            src={property.images[imgIdx].src}
                            alt={property.images[imgIdx].alt}
                            fill
                            className="object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          {property.badge && (
                            <div className="absolute top-2 left-2 bg-white text-gray-800 text-xs font-bold px-2 py-0.5 rounded-full shadow-sm">
                              {property.badge}
                            </div>
                          )}
                          <button
                            onClick={(e) => toggleLike(e, property.id)}
                            className="absolute top-2 right-2 p-1 transition-transform hover:scale-110"
                          >
                            <svg
                              className={`w-5 h-5 drop-shadow-md ${liked ? 'text-[#4088C6] fill-[#4088C6]' : 'text-white fill-white/30 stroke-white'}`}
                              viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                            </svg>
                          </button>
                        </div>
                        <div className="flex-1 min-w-0" onClick={() => window.location.href = `/property/${property.id}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                {property.isSuperhost && <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">Superhost</span>}
                                <span className="text-xs text-gray-500">{property.type}</span>
                              </div>
                              <h3 className="text-base font-semibold text-gray-900">{property.name}</h3>
                              <p className="text-sm text-gray-500">{property.location}, {property.country}</p>
                              <p className="text-xs text-gray-400 mt-1">{property.bedrooms} bed · {property.maxGuests} guests max</p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <svg className="w-3.5 h-3.5 text-gray-900 fill-gray-900" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              <span className="text-sm font-semibold text-gray-900">{property.rating.toFixed(2)}</span>
                              <span className="text-xs text-gray-400">({property.reviews})</span>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {property.amenities.slice(0, 4).map((a) => (
                              <span key={a} className="text-xs text-gray-600 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full">{a}</span>
                            ))}
                          </div>
                          <div className="flex items-center justify-between mt-3">
                            <div>
                              <span className="text-base font-bold text-gray-900">{property.currency} {property.price.toLocaleString()}</span>
                              <span className="text-sm text-gray-500"> / night</span>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); setBookingProperty(property); }}
                              className="text-sm font-semibold text-white bg-[#4088C6] hover:bg-[#2D6FA8] px-4 py-1.5 rounded-full transition-all"
                            >
                              Reserve
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {/* Mobile filter drawer */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-[100] flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMobileFilters(false)} />
          <div className="relative ml-auto w-full max-w-sm bg-white h-full overflow-y-auto p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900">Filters</h2>
              <button onClick={() => setShowMobileFilters(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <FilterPanel {...filterPanelProps} />
            <button
              onClick={() => setShowMobileFilters(false)}
              className="w-full mt-6 bg-[#4088C6] hover:bg-[#2D6FA8] text-white font-semibold py-3.5 rounded-xl transition-all"
            >
              Show {sorted.length} results
            </button>
          </div>
        </div>
      )}

      {bookingProperty && (
        <BookingModal
          property={bookingProperty}
          onClose={() => setBookingProperty(null)}
        />
      )}
    </div>
  );
}

export default function SearchPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-[#4088C6] border-t-transparent rounded-full animate-spin" /></div>}>
      <SearchResultsContent />
    </Suspense>
  );
}
